import React, { useEffect, useMemo, useRef, useState } from "react";
import { NETWORK_GROUPS } from "../../data/apiReference";
import ChainLogo from "./ChainLogo";

// Logo for a group — reuse ChainLogo's monogram fallback via a chain-shaped stub.
const GroupLogo = ({ group, size = 22 }) => (
  <ChainLogo chain={{ name: group.name, logo: group.logo, family: group.family }} size={size} />
);

const GridGlyph = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden="true">
    <rect x="3" y="3" width="5" height="5" rx="1.2" />
    <rect x="12" y="3" width="5" height="5" rx="1.2" />
    <rect x="3" y="12" width="5" height="5" rx="1.2" />
    <rect x="12" y="12" width="5" height="5" rx="1.2" />
  </svg>
);

const Check = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CountBadge = ({ n, active }) => (
  <span
    className={`ml-auto shrink-0 rounded-md px-1.5 py-px font-mono text-[10px] font-bold tabular-nums ${
      active ? "bg-primary/20 text-primary" : "bg-white/[0.06] text-gray-400"
    }`}
    title={`${n} network${n === 1 ? "" : "s"}`}
  >
    {n}
  </span>
);

// Ecosystem selector combobox for the Networks reference sidebar. Counts are
// networks (chain ids) inside the group, not Datanodes slugs.
// value: group id or null (= all networks). onChange(groupId | null).
const NetworkSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = NETWORK_GROUPS.find((g) => g.id === value) || null;

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = { id: null, name: "All networks", count: NETWORK_GROUPS.reduce((n, g) => n + g.networks.length, 0) };
    const list = NETWORK_GROUPS.filter(
      (g) =>
        !q ||
        g.name.toLowerCase().includes(q) ||
        g.chains.some((c) => c.slug.includes(q) || c.name.toLowerCase().includes(q) || String(c.chainId ?? "").toLowerCase() === q),
    ).map((g) => ({ id: g.id, name: g.name, count: g.networks.length, group: g }));
    return q ? list : [all, ...list];
  }, [query]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Focus search + reset cursor when opening.
  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${cursor}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const choose = (id) => {
    onChange(id);
    setOpen(false);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[cursor]) choose(options[cursor].id);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select network"
        className={`flex w-full items-center gap-2.5 rounded-xl border bg-white/[0.04] px-3 py-2 text-left text-sm transition-colors ${
          open ? "border-primary/60 shadow-glow-sm" : "border-white/10 hover:border-white/30"
        }`}
      >
        {selected ? (
          <GroupLogo group={selected} size={22} />
        ) : (
          <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-primary">
            <GridGlyph className="h-3.5 w-3.5" />
          </span>
        )}
        <span className="truncate font-semibold text-white">{selected ? selected.name : "All networks"}</span>
        <CountBadge n={selected ? selected.networks.length : NETWORK_GROUPS.reduce((n, g) => n + g.networks.length, 0)} active={!!selected} />
        <svg viewBox="0 0 20 20" className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-space-card/95 shadow-2xl backdrop-blur-md">
          <div className="border-b border-white/[0.06] p-2">
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCursor(0);
              }}
              onKeyDown={onInputKey}
              placeholder="Search networks…"
              aria-label="Search networks"
              className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm text-white placeholder-gray-500 outline-none focus:border-primary/50"
            />
          </div>
          <ul ref={listRef} role="listbox" aria-label="Networks" className="m-0 max-h-72 list-none overflow-y-auto p-1.5">
            {options.length === 0 && <li className="px-3 py-2 text-xs text-gray-500">No network matches “{query}”.</li>}
            {options.map((opt, i) => {
              const isSelected = opt.id === value;
              const isCursor = i === cursor;
              return (
                <li key={opt.id ?? "__all"} role="option" aria-selected={isSelected} data-index={i}>
                  <button
                    type="button"
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => choose(opt.id)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm transition-colors ${
                      isCursor ? "bg-white/[0.06] text-white" : "text-gray-300"
                    }`}
                  >
                    {opt.group ? (
                      <GroupLogo group={opt.group} size={22} />
                    ) : (
                      <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-primary">
                        <GridGlyph className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <span className={`truncate ${isSelected ? "font-semibold text-primary" : ""}`}>{opt.name}</span>
                    <CountBadge n={opt.count} active={isSelected} />
                    <span className={`w-4 shrink-0 text-primary ${isSelected ? "opacity-100" : "opacity-0"}`}>
                      <Check />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NetworkSelect;
